import Util from '../utils/util';
import Source from './Source';
import pbfUtil from '../pbf/pbfUtil';
import Feature from '../render/Feature';

/**
 * 瓦片数据源
 * 以瓦片为单位加载数据
 * 支持配置固定的placeholders参数
 * 支持将多个不同瓦片图层同一位置的瓦片请求合并成一个请求
 * 请求参数支持动态融合每个瓦片中数组类型的mergeParamaters
 * 支持在图层中配置数据转换函数，在瓦片请求到数据之后，对数据进行转换
 */
export default class TileSource extends Source {
    /**
     * 数据源对象的初始化方法.
     * @param {String} name    - 数据源名称
     * @param {Object} options - 数据源配置
     * @returns {undefined}
     */
    constructor(name, config, options) {
        super(name, config, options);

        /**
         * pbf的坐标的放大率
         * 在responseType = 'arraybuffer'起作用
         * @type {number}
         */
        this._ratio = config.ratio || 16;
        /**
         * 坐标维度: 2|3
         * @type {number}
         */
        this._dimension = config.dimension || 2;
        /**
         * 坐标类型，1：瓦片坐标，2：平面像素坐标，3：经纬度坐标
         * coordinate system type
         * @type {number}
         */
        this._cst = config.cst || 1;
        /**
         * 转换函数
         * @type {function}
         */
        this._transform = config.transform;
        /**
         * 瓦片集合对象
         * @type {object}
         */
        this._tiles = {};
        if (config.scheme && config.scheme.toUpperCase() == 'WFS') {
            this._wfsParam = {
                service: 'WFS',
                request: 'GetFeature',
                version: '1.0.0',
                typeName: '', // 外部填充
                outputFormat: 'application/json',
            }
        }
    }

    onAdd(controller) {
        super.onAdd(controller);

        this._geojsonConverter = this._map.GeojsonConverter;
    }

    onRemove(controller) {
        this._geojsonConverter = null;
        
        super.onRemove(controller);
    }

    // geoServer的参数格式
    getWfsFilterParam(filterParam, bbox) {
        let bboxKey = filterParam['geometryKey'];
        if (!bboxKey) {
            throw new Error('wfs图层配置缺少geometryKey配置项!');
        }
        let cqlFilter = `BBOX(${bboxKey}, ${bbox[0]},${bbox[1]}, ${bbox[2]},${bbox[3]}, 'EPSG:4326')`;
        if (filterParam.isDefine && filterParam.defineSql) { // 允许传入自定义的sql
            if (typeof filterParam.defineSql == 'function') {
                cqlFilter += ' AND ' + filterParam.defineSql();
            } else {
                cqlFilter += ` AND ${filterParam.defineSql}`;
            }
        } else {
            Object.keys(filterParam).forEach(key => {
                if (!(filterParam[key] == null || filterParam[key] === '' || key === 'geometryKey')) {
                    cqlFilter += ` AND ${key}='${filterParam[key]}'`;
                }
            });
        }
        
        return cqlFilter;
    }

    /**
     * 创建请求
     * @param   {Array} tilesObj - 请求的瓦片信息
     * @returns {undefined}
     */
    _createRequest(tiles) {
        const tile = tiles[0];
        const defaultPlaceholders = {
            z: tile.z,
            x: tile.x,
            y: tile.y
        };

        const params = {};

        tiles.forEach(t => {
            const temp = t.mergeParameters;
            if (temp) {
                for (const [k, v] of Object.entries(temp)) {
                    params[k] = params[k] || [];
                    if (!Util.isArray(params[k]) || !Util.isArray(v)) {
                        throw new Error(`mergeParameters的值必须是数组：${k}`);
                    }

                    params[k].push(...v);
                }
            }
        });

        for (const [k, v] of Object.entries(params)) { // 参数去重
            params[k] = [...new Set(v)];
        }

        if (this._wfsParam) {
            const bbox = this._map.TileManager.getTilebbox(tile);
            if (params.filter) {
                this._wfsParam.CQL_FILTER = this.getWfsFilterParam(params.filter[0], bbox);
                delete params['filter'];
            } else {
                this._wfsParam.BBOX = bbox.toString();
            }
            
            const wfsParam = Object.assign(this._wfsParam, params);
            wfsParam.typeName = this._wfsParam.typeName.toString();
            const _param = Object.keys(wfsParam).map(key => `${key}=${wfsParam[key]}`).join('&');
            this._url = this._url.split('?')[0] + '?' + _param;
        }
        
        const placeholders = Object.assign(defaultPlaceholders, params);

        return super._createRequest(placeholders);
    }

    loadTile(layerTile) {
        let sourceTile;
        if (this._tiles.hasOwnProperty(layerTile.name)) {
            sourceTile = this._tiles[layerTile.name];
            sourceTile.tiles.push(layerTile);
        } else {
            sourceTile = {
                tiles: [layerTile],
                loadTile: Util.debounceP(this, this._loadTile), // 延迟异步请求到下一轮事件循环
                _status: 0 // 0:未发起请求；1：请求执行中；2：请求完成；3：数据处理完成；-1：数据请求出错
            };
            this._tiles[layerTile.name] = sourceTile;
        }

        return sourceTile.loadTile(layerTile);
    }

    _loadTile(layerTile) {
        const sourceTile = this._tiles[layerTile.name];
        if (!sourceTile) {
            return null;
        }

        const req = this._createRequest(sourceTile.tiles);

        sourceTile.request = req;

        sourceTile._status = 1;

        return req.promise
            .then(res => this._tileLoaded(res, sourceTile))
            .catch(e => {
                sourceTile._status = -1;
            });
    }

    removeTile(layerTile) {
        const sourceTile = this._tiles[layerTile.name];
        if (sourceTile) {
            sourceTile.tiles.splice(sourceTile.tiles.indexOf(layerTile), 1);
            if (sourceTile.tiles.length === 0) {
                if (sourceTile._status === 1) {
                    sourceTile.request.abort();
                }
                
                delete this._tiles[layerTile.name];
            }
        }
    }

    _tileLoaded(res, sourceTile) {
        sourceTile._status = 2; // 请求执行完成完成

        if (!res) {
            return null;
        }

        let data;
        if (this._responseType === 'json') {
            if (res.dataType && res.dataType === 'pbf' && res.data) {
                data = pbfUtil.base64ToJson(res.data, this._ratio, this._dimension);
            } else {
                data = res.data || res.features || null;
            }
        } else if (this._responseType === 'arraybuffer') {
            data = pbfUtil.arrayBufferToJson(res, this._ratio, this._dimension);
        } else {
            throw new Error(`未知的responseType: ${this._responseType}`);
        }

        if (!data) {
            return null;
        }

        // 执行外部转换函数
        if (Util.isFunction(this._transform)) {
            data = this._transform(data);
        }
       
        const temp = {};
        for (const [k, v] of Object.entries(data)) {
            temp[k] = v.map(d => {
                if (Feature.is(d)) {
                    return d;
                }
                return new Feature(d.geometry, d.properties);
            });
        }
        sourceTile.data = temp;

        // 将瓦片坐标转换为经纬度坐标
        const tileInfo = sourceTile.tiles[0];
        let convertFunc;
        if (this._cst === 1) {
            convertFunc = this._geojsonConverter.getTilePointToLngLat(tileInfo);
        } else if (this._cst === 2) {
            convertFunc = this._geojsonConverter.getPixelToLngLat();
        }
        if (convertFunc) {
            for (const rec of Object.values(sourceTile.data)) {
                rec.forEach(f => {
                    f.geometry = this._geojsonConverter.convert(f.geometry, convertFunc);
                });
            }
        }

        sourceTile._status = 3; // 数据处理完成

        return sourceTile.data;
    }
}
